import * as Sentry from '@sentry/vue'

/**
 * Checks if two URLs match. This function compares the URLs directly, after removing the protocol, and after removing the origin.
 * Note: The request URL can come without protocol &/ origin, because `fetch` and `XMLHttpRequest`
 * can be called without them, in which case the document's protocol and/or origin are used.
 * E.g. the slack web app does this.
 *
 * @param {string} requestUrl - The URL of the request.
 * @param {string} requestToMatchUrl - The URL of the request to match.
 * @returns {boolean} True if the URLs match in any of the comparisons, false otherwise.
 */
export function isURLMatching(requestUrl, requestToMatchUrl) {
	return (
		requestUrl === requestToMatchUrl ||
		removeProtocolFromUrl(requestUrl) === removeProtocolFromUrl(requestToMatchUrl) ||
		getUrlPathIfPresent(requestUrl) === getUrlPathIfPresent(requestToMatchUrl)
	)
}

/**
 * Removes the origin (protocol, hostname, and port) from a given URL string, if a path is present.
 *
 * @param {string} urlString - The URL string from which to remove the origin.
 * @returns {string} The URL string without the origin. If the input is not a string, the input is converted to a string and returned. If the URL is invalid, the original urlString is returned.
 */
export function getUrlPathIfPresent(urlString) {
	if (typeof urlString !== 'string') return String(urlString)
	try {
		const url = new URL(urlString)
		const urlWithoutOrigin = urlString.replace(url.origin, '')
		if (urlWithoutOrigin === '') return urlString
		if (urlWithoutOrigin === '/') return urlString
		return urlWithoutOrigin
	} catch (error) {
		return urlString
	}
}

/**
 * Removes the protocol (http:// or https://) from a given URL.
 *
 * @param {string} url - The URL from which to remove the protocol.
 * @returns {string} The URL without the protocol. If the input is not a string, the input is returned as is.
 */
export function removeProtocolFromUrl(url, httpProtocolRegexp = /^https?:/) {
	if (typeof url !== 'string') return url
	return url.replace(httpProtocolRegexp, '')
}

/**
 * Checks if two request methods match. This function compares the methods after converting them to uppercase.
 *
 * @param {string} requestMethod - The method of the request.
 * @param {string} requestToMatchMethod - The method of the request to match.
 * @returns {boolean} True if the methods match, false otherwise.
 */
export function isMethodMatching(requestMethod, requestToMatchMethod) {
	if (typeof requestMethod !== 'string' || typeof requestToMatchMethod !== 'string') return false
	return requestMethod.toUpperCase().trim() === requestToMatchMethod.toUpperCase().trim()
}

/**
 * Checks if the request body from the Extension API matches the request body from the instrumentation.
 * This function handles various edge cases such as missing request bodies, different representations of FormData,
 * and different types of request bodies (raw, error, object, string).
 * It also handles cases where the request body is a URL encoded string or an object.
 * If an error occurs during the comparison, it is logged and sent to Sentry, and the function returns false.
 *
 * @param {Object} params - The parameters for the function.
 * @param {Object|string|null} params.requestBodyFromExtensionAPI - The request body from the Extension API. It can be an object, a string, or null.
 * @param {Object|string|null} params.requestBodyFromInstrumentation - The request body from the instrumentation. It can be an object, a string, or null.
 * @returns {boolean} True if the request bodies match, false otherwise.
 */
export function isRequestBodyMatching({requestBodyFromExtensionAPI, requestBodyFromInstrumentation}) {
	// both recorded requests don't have a request body -> match
	if (!requestBodyFromExtensionAPI && !requestBodyFromInstrumentation) return true

	// `XMLHttpRequest` monkey-patch records stringified empty object as empty request body
	if (!requestBodyFromExtensionAPI && requestBodyFromInstrumentation === '{}') return true

	// Assume a match when the `WebRequest` API didn't record a response,
	// but the `fetch` / `XMLHttpRequest` monkey-patches did.
	// Reason: Chrome is often leaving out `requestBody`, e.g. for PATCH requests, see:
	//  - https://bugs.chromium.org/p/chromium/issues/detail?id=1081423&q=requestBody&can=2
	//  - https://bugs.chromium.org/p/chromium/issues/detail?id=759498&q=requestBody&can=2
	if (!requestBodyFromExtensionAPI && requestBodyFromInstrumentation) return true

	// the `WebRequest` API recorded a request body, but `fetch` / `XMLHttpRequest` didn't -> no match
	if (requestBodyFromExtensionAPI && !requestBodyFromInstrumentation) return false

	try {
		// `network.js` records FormData as "dictionary" object, where the value is an array - see `onBeforeRequest`
		// in the `WebRequest` API docs: https://developer.chrome.com/docs/extensions/reference/webRequest/#type-FormDataItem;
		// The monkey-patches in `instrumentNetwork` record FormData either as object or as URL encoded query string.
		// The code block below returns whether any of the `network.js` request's FormData key=>value-pairs is not fully matching
		// the potential response, accounting for the different possible FormData representations in the response's request body.
		if (requestBodyFromExtensionAPI.formData) {
			const isRequestFormDataUrlEncoded = typeof requestBodyFromInstrumentation === 'string'
			// Requests with FormData request body result in an object like below when recorded with `WebRequest` API:
			//   {email: ['a@b.com'], name: ['Random Person Name']}
			// From `fetch` / `XMLHttpRequest`, the same request body is recorded as:
			//   `name=Random+Person+Name&email=a%40b.com`
			for (const [key, value] of Object.entries(requestBodyFromExtensionAPI.formData)) {
				// Transforms ['Random Person Name'] to 'Random Person Name'
				const formDataItemValue = value.join()
				if (isRequestFormDataUrlEncoded) {
					try {
						// Example: decodeURIComponent('name=Random+Person+Name&email=a%40b.com').includes(`name=Random+Person+Name`)
						const isMatchWhenUrlEncoded = decodeURIComponent(requestBodyFromInstrumentation).includes(
							`${key}=${formDataItemValue.replaceAll(' ', '+')}`
						)
						if (!isMatchWhenUrlEncoded) return false
					} catch (error) {
						// When the URI is malformed and can't be decoded, there's no possibility to compare, and therefore no basis to assume a match
						return false
					}
				}
				// I've yet to experience that the value array actually contains more than one value.
				// I don't know but assume that in this case the responseRequestBody's value for the key would also be an array.
				else if (typeof requestBodyFromInstrumentation[key] === 'string') {
					if (requestBodyFromInstrumentation[key] !== formDataItemValue) return false
				} else if (Array.isArray(requestBodyFromInstrumentation[key])) {
					if (requestBodyFromInstrumentation[key].join() !== formDataItemValue) return false
				} else {
					return false
				}
			}
			return true
		}

		// Compare the request body from the `WebRequest` API request and the `fetch` / `XMLHttpRequest` request.
		requestBodyFromExtensionAPI =
			requestBodyFromExtensionAPI.raw || requestBodyFromExtensionAPI.error || requestBodyFromExtensionAPI || ''

		if (typeof requestBodyFromExtensionAPI === 'object') {
			requestBodyFromExtensionAPI = JSON.stringify(requestBodyFromExtensionAPI)
		}
		if (typeof requestBodyFromInstrumentation === 'object') {
			requestBodyFromInstrumentation = JSON.stringify(requestBodyFromInstrumentation)
		}

		// Compare after removing non-word characters to ignore formatting mismatches.
		return (
			String(requestBodyFromExtensionAPI).replace(/\W/gi, '') ===
			String(requestBodyFromInstrumentation).replace(/\W/gi, '')
		)
	} catch (error) {
		// eslint-disable-next-line no-console
		console.error(error)
		Sentry.captureException(error)
	}
	return false
}
